import { isAfter, isBefore } from "date-fns";

import {
  AssessmentSettingsFragment,
  AssessmentType,
  RequirementsFragment,
  TaskKind,
} from "@/generated/graphql";

import { isExam, isExamClosed } from "./exam-utils";

/**
 * Determine whether the feedback date for this assessment has not passed yet.
 * NOTE: If an exam has noExamFeedbackDate true returns true.
 */
export function isBeforeFeedbackDate(
  settings: Pick<
    AssessmentSettingsFragment,
    "assessmentType" | "feedbackDate" | "feedbackDateDisabled"
  >
): boolean {
  const { feedbackDate, feedbackDateDisabled } = settings;
  if (feedbackDate == null) return true;
  if (isExam(settings) && feedbackDateDisabled) return true;

  return isBefore(new Date(), new Date(feedbackDate));
}

/**
 * @returns whether the assessment is no longer accepting submissions
 * (ignoring any special considerations/extensions)
 */
export function isAssessmentClosed(
  settings: Pick<
    AssessmentSettingsFragment,
    | "assessmentType"
    | "dueDate"
    | "examReadingTime"
    | "examWritingTime"
    | "examTiming"
    | "examStartDate"
    | "examEndDate"
  >
): boolean {
  if (isExam(settings)) {
    return isExamClosed(settings);
  }
  return (
    settings.dueDate != null && isAfter(new Date(), new Date(settings.dueDate))
  );
}

export type AssessmentNoun = "Exam" | "Assignment" | "Assessment";

/**
 * Select the noun used to refer to this released assessment.
 *
 * Individual tasks (standard assignments and exams) use their specific names —
 * "Assignment" or "Exam" — because these are established, meaningful terms for
 * students in those contexts.
 *
 * Any other task kind resolves to the neutral "Assessment".
 */
export function selectAssessmentNoun(
  settings: Pick<
    AssessmentSettingsFragment,
    "assessmentType" | "vivaEnabled" | "groupAssessmentEnabled"
  > | null
): AssessmentNoun {
  if (
    settings?.vivaEnabled === false &&
    settings?.groupAssessmentEnabled === false
  ) {
    if (settings != null && isExam(settings)) {
      return "Exam";
    }
    return "Assignment";
  }
  return "Assessment";
}

export function selectAssessmentNounForTask(task: {
  kind: TaskKind;
  requirements: Pick<RequirementsFragment, "assessmentType">;
}): AssessmentNoun {
  if (task.kind !== TaskKind.Individual) {
    return "Assessment";
  }
  if (task.requirements.assessmentType === AssessmentType.Exam) {
    return "Exam";
  }
  return "Assignment";
}
